// MEGBESZÉLÉS-RÉSZLET „peek" kártya — a naptár-eseményre kattintva nyíló kis panel (Google Calendar /
// Outlook minta): időpont, helyszín, online-csatlakozás, szervező, RÉSZTVEVŐK (avatar + szerep + RSVP), a
// saját RSVP-vezérlő, és egy „Szerkesztés" gomb, ami a szerkesztő-sheetet nyitja.
//
// MIÉRT EZ A MINTA (user-döntés): a résztvevőket MINDEN nézetben látni kell, de a szerkesztő-sheet
// nem jó hely rá — ott a multiselect már megmutatja a kiválasztottakat, a panel viszont hosszabbá tette a
// formot. A naptárakban a bevett megoldás a betekintő kártya: egy kattintás a részletekhez, a szerkesztés
// pedig egy explicit gomb. Fontos következmény: a SAJÁT RSVP-vezérlő ide került (a sheetből kivéve) — ez az
// egyetlen felület, ahol a résztvevő a saját válaszát a naptárból állíthatja.
//
// A pozicionálás a KATTINTOTT esemény-elem téglalapjához kötött (Radix `PopoverAnchor` egy fix-pozíciós,
// nulla-interakciós horgony-elemen): a kártya ott nyílik, ahol a felhasználó kattintott, és a Radix maga
// fordítja át a másik oldalra, ha nem fér ki.
import { type MeetingRowSchema } from '@atlas/contracts';
import {
  ArrowPathIcon,
  ClockIcon,
  MapPinIcon,
  UserCircleIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { MeetingParticipantsPanel } from '@/components/meetings/meeting-participants-panel';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { canModifyMeeting } from '@/features/meetings/calendar-map';
import { describeSeries } from '@/features/meetings/recurrence';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';
import { formatName } from '@/lib/format-name';

type MeetingRow = z.infer<typeof MeetingRowSchema>;

/** A kattintott esemény-elem téglalapja (viewport-koordináták) — a kártya horgonya. */
export interface PeekAnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface MeetingPeekCardProps {
  /** A megjelenítendő sor; `null` → a kártya nem renderel tartalmat (zárt állapot). */
  meeting: MeetingRow | null;
  anchor: PeekAnchorRect | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A bejelentkezett identity (token `sub`) — a saját RSVP-vezérlő ehhez kötött. */
  currentUserId: string | null;
  /** Van-e `meetings.manage` jog — enélkül nincs „Szerkesztés" gomb (a kártya olvasható marad). */
  canManage: boolean;
  /**
   * Van-e `meetings.modify_any` jog (MÁS megbeszélésének módosítása). Enélkül a „Szerkesztés" CSAK a saját
   * soron aktív — idegen soron TILTOTT, tooltippel. A fal a BFF-en van (403); ez a felület-oldali kényelem.
   */
  canModifyAny: boolean;
  onEdit: (meeting: MeetingRow) => void;
}

/** Egy ikonos meta-sor (idő / helyszín / online / szervező) — a kártya sorainak egyetlen tipográfiája. */
function MetaRow({
  icon: Icon,
  children,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}): JSX.Element {
  return (
    // KÖZÉPRE igazított ikon: a 16px-es ikon a szöveg SORÁNAK közepére kerül (`items-center` + a szöveg
    // `leading-5`-e = az ikon magassága). Korábban `items-start` + `mt-0.5` volt: az ikon a sor tetejéhez
    // tapadt, tehát optikailag feljebb ült, mint a mellette lévő érték (a user ezt jelezte).
    <div className="flex items-center gap-2">
      <Icon className="text-muted-foreground size-4 shrink-0" />
      <div className="min-w-0 flex-1 leading-5">{children}</div>
    </div>
  );
}

export function MeetingPeekCard({
  meeting,
  anchor,
  open,
  onOpenChange,
  currentUserId,
  canManage,
  canModifyAny,
  onEdit,
}: MeetingPeekCardProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // Időpont: a megjelenítési zónában (Europe/Budapest). A NAP egyszer szerepel — a vég csak az időt írja ki,
  // kivéve, ha átnyúlik a következő napra (akkor ott is kell a dátum, különben félreérthető).
  const start = meeting?.startsAt ?? null;
  const end = meeting?.endsAt ?? null;
  const sameDay =
    start !== null &&
    end !== null &&
    formatDateTime(start, i18n.language, DEFAULT_TIME_ZONE, { dateStyle: 'short' }) ===
      formatDateTime(end, i18n.language, DEFAULT_TIME_ZONE, { dateStyle: 'short' });
  const when =
    start === null
      ? ''
      : `${formatDateTime(start, i18n.language, DEFAULT_TIME_ZONE, {
          dateStyle: 'full',
          timeStyle: 'short',
        })}${
          end === null
            ? ''
            : ` – ${formatDateTime(
                end,
                i18n.language,
                DEFAULT_TIME_ZONE,
                sameDay ? { timeStyle: 'short' } : { dateStyle: 'full', timeStyle: 'short' },
              )}`
        }`;

  // A szervező MEGJELENÍTETT neve: a strukturált részekből a nyelv szerinti sorrendben, azok hiányában a
  // platform megjelenítendő neve (`organizerName`). Nincs szervező → `null` (a hívó helyőrzőt ír ki).
  const organizerLabel =
    meeting?.organizerName == null || meeting.organizerName === ''
      ? null
      : formatName(
          {
            givenName: meeting.organizerGivenName,
            familyName: meeting.organizerFamilyName,
            displayName: meeting.organizerName,
          },
          i18n.language,
        );

  // Módosíthatom-e EZT a sort? (saját sor VAGY `meetings.modify_any`) — ugyanaz a szabály, mint a BFF-en.
  const mayModify = meeting !== null && canModifyMeeting(meeting, currentUserId, canModifyAny);

  const locationLabel =
    meeting?.locationKey != null && meeting.locationKey !== ''
      ? t(`meetings.locationLabel.${meeting.locationKey}`, { defaultValue: meeting.locationKey })
      : null;
  // Az online-link: a három típus közül az ELSŐ kitöltött (a form egyszerre csak egyet enged).
  const onlineLink =
    [meeting?.zoomLink, meeting?.googleMeetLink, meeting?.microsoftTeamsLink].find(
      (l) => l != null && l !== '',
    ) ?? null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* Horgony: nulla-interakciós, fix-pozíciós elem a kattintott esemény téglalapján. */}
      <PopoverAnchor asChild>
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: anchor?.top ?? 0,
            left: anchor?.left ?? 0,
            width: anchor?.width ?? 0,
            height: anchor?.height ?? 0,
            pointerEvents: 'none',
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        // a kártya sosem lóghat ki a képből: a Radix a képernyő-széltől ennyi távolságot tart (oldal-fordítás +
        // eltolás); a széles eseménynél a horgony a kattintás pontja (`peekAnchorFor`)
        collisionPadding={12}
        className="w-[21rem] max-w-[calc(100vw-1.5rem)] p-0"
        // A kártya a naptár-rács FÖLÖTT ül, és a fókusz a megnyitáskor rajta marad (Radix default), tehát
        // billentyűzettel is bejárható; Esc/kattintás kívülre zárja.
      >
        {meeting && (
          // A teszt-horgony (`data-testid` + `data-meeting-id`) ugyanaz a minta, mint a szerkesztő-sheeten:
          // a wiring-teszt így a MEGJELENÍTETT sort tudja ellenőrizni, nem a belső állapotot.
          <div className="flex flex-col" data-testid="meeting-peek" data-meeting-id={meeting.id}>
            <div className="flex items-start gap-2 px-3 pt-3">
              <p className="min-w-0 flex-1 text-sm font-semibold leading-snug">{meeting.title}</p>
              <button
                type="button"
                aria-label={t('common.close')}
                // A fókusz-jelzés a design-system ringje (a böngésző kék default outline-ja helyett) — a Radix
                // a megnyitáskor ide teszi a fókuszt, tehát ez a gyűrű a kártya első benyomása.
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -mt-0.5 shrink-0 rounded p-0.5 focus:outline-none focus-visible:ring-1"
                onClick={() => onOpenChange(false)}
              >
                <XMarkIcon className="size-4" />
              </button>
            </div>

            <div className="space-y-1.5 px-3 py-2.5 text-sm">
              {when !== '' && <MetaRow icon={ClockIcon}>{when}</MetaRow>}
              {locationLabel !== null && <MetaRow icon={MapPinIcon}>{locationLabel}</MetaRow>}
              {meeting.series && (
                <MetaRow icon={ArrowPathIcon}>
                  <span>
                    {t('meetings.recurrence.seriesBadge')}:{' '}
                    {describeSeries(meeting.series, t, i18n.language)}
                  </span>
                </MetaRow>
              )}
              {onlineLink !== null && (
                <MetaRow icon={VideoCameraIcon}>
                  <a
                    href={onlineLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {t('meetings.peek.join')}
                  </a>
                </MetaRow>
              )}
              {/* SZERVEZŐ: csak NÉV, profilkép NÉLKÜL (user-döntés) — a kártyán az avatar-nyelv a
                  RÉSZTVEVŐ-listát jelöli; a szervező egy meta-sor a többi (idő/helyszín/link) mellett, ott a
                  kép csak zajt adna. A név a nyelvhelyes sorrendben (`formatName`: magyarul vezetéknév elöl). */}
              <MetaRow icon={UserCircleIcon}>
                {organizerLabel !== null ? (
                  <span>{organizerLabel}</span>
                ) : (
                  <span className="text-muted-foreground">{t('meetings.noOrganizer')}</span>
                )}
              </MetaRow>
            </div>

            {/* Résztvevők + saját RSVP — a KÖZÖS panel (nincs második implementáció). Sok résztvevőnél a
                blokk görgethető, hogy a kártya ne nyúljon ki a képernyőből. */}
            <div className="max-h-64 overflow-y-auto px-3 pb-3">
              <MeetingParticipantsPanel meeting={meeting} currentUserId={currentUserId} />
            </div>

            {canManage && (
              <div className="flex justify-end px-3 pb-3">
                {mayModify ? (
                  <Button variant="brand" type="button" size="sm" onClick={() => onEdit(meeting)}>
                    {t('common.edit')}
                  </Button>
                ) : (
                  // TILTOTT gomb + magyarázat: a felhasználó lássa, hogy a művelet LÉTEZIK, csak nem az övé
                  // (egy eltüntetett gomb helyén a felhasználó hibát keres). A cím a11y-célra is szolgál.
                  <Button
                    variant="brand"
                    type="button"
                    size="sm"
                    disabled
                    title={t('meetings.peek.notOwnHint')}
                    aria-label={t('meetings.peek.notOwnHint')}
                  >
                    {t('common.edit')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
